import { SLOT_LABELS, TICK_MS } from "@pd/shared";
import type { GearCombatStats, ItemDef } from "@pd/shared";

const LABELS: Record<string, string> = {
  meleeAttack: "Melee attack", magicAttack: "Magic attack",
  meleeDamageBonusPct: "Melee damage", magicDamageBonusPct: "Magic damage",
  meleeDef: "Melee defence", magicDef: "Magic defence", maxHp: "Health",
  range: "Range",
};
const signed = (n: number): string => `${n > 0 ? "+" : ""}${n}`;
let tooltipId = 0;

/** Display categories only; the sim still consumes the exact authored ticks. */
export function attackSpeedLabel(ticks: number): string {
  const ms = ticks * TICK_MS;
  return ms < 2400 ? "Fast" : ms < 3500 ? "Medium" : "Slow";
}

function icon(kind: string): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 20 20");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("gear-stat-icon");
  const path = document.createElementNS(svg.namespaceURI, "path");
  const shapes: Record<string, string> = {
    attack: "M13 2h5v5L8 17l-3-3L15 4M3 11l6 6M5 15l-3 3",
    magic: "M10 2 12 7l5 3-5 3-2 5-2-5-5-3 5-3Z",
    damage: "m11 1-6 10h5l-1 8 6-11h-5Z",
    defence: "M3 3h14v7c0 4-7 8-7 8S3 14 3 10ZM10 3v15",
    health: "M10 17 3 10C-1 4 5 0 10 6c5-6 11-2 7 4Z",
    speed: "M10 3a7 7 0 1 0 7 7M10 6v4l4 2M8 1h4",
    range: "M2 10h16M6 6l-4 4 4 4M14 6l4 4-4 4",
  };
  path.setAttribute("d", shapes[kind] ?? shapes.damage!);
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "1.7");
  path.setAttribute("stroke-linejoin", "round");
  svg.append(path);
  return svg;
}

function styles(): void {
  if (document.getElementById("gear-stat-styles")) return;
  const style = document.createElement("style");
  style.id = "gear-stat-styles";
  style.textContent = `
    .item-stat-tooltip { position:fixed; z-index:12000; pointer-events:none; width:238px; max-width:calc(100vw - 16px); box-sizing:border-box; padding:10px; border:1px solid #9c8053; border-radius:2px; background:linear-gradient(#28302c,#171d1b); color:#d8dfd4; box-shadow:inset 0 0 0 2px #1119,0 5px 16px #000b; font:12px/1.4 system-ui,sans-serif; text-align:left; }
    .item-stat-tooltip[hidden] { display:none; }
    .item-stat-tooltip > strong { display:block; color:#e7c16d; font:600 14px Georgia,serif; }
    .item-stat-tooltip > small { display:block; color:#a5aca0; margin:2px 0 8px; text-transform:capitalize; }
    .item-stats { display:grid; gap:3px; font:12px/1.35 system-ui,sans-serif; }
    .item-stat-row { display:flex; align-items:center; justify-content:space-between; gap:12px; min-height:23px; padding:1px 5px; background:#090e0c55; border:1px solid #ffffff08; }
    .item-stat-key { display:flex; align-items:center; gap:6px; color:#aab8ab; }
    .item-stat-value { color:#e8e4ce; white-space:nowrap; font-weight:600; font-variant-numeric:tabular-nums; }
    .item-stat-negative { color:#ed9b87; } .item-stat-positive,.item-stat-fast { color:#b1d7a1; }
    .item-stat-medium { color:#e7c16d; } .item-stat-slow { color:#c6ad90; }
    .gear-stat-icon { flex:none; width:14px; height:14px; }
    .gear-stats { margin-top:7px; padding-top:6px; border-top:1px solid #64705b; font:11px/1.4 system-ui,sans-serif; }
    .gear-stat-head,.gear-stat-row { display:grid; grid-template-columns:minmax(72px,1fr) minmax(48px,0.7fr) minmax(48px,0.7fr); align-items:center; gap:5px; }
    .gear-stat-head { margin-bottom:4px; color:#adbaac; font-size:10px; }
    .gear-stat-head > span { display:flex; align-items:center; justify-content:center; gap:3px; }
    .gear-stat-head > span:last-child { color:#a1c5dc; }
    .gear-stat-row { margin-bottom:3px; }
    .gear-stat-row > span:first-child { display:flex; align-items:center; gap:5px; color:#adb8a9; }
    .gear-stat-row > span:not(:first-child) { padding:3px 5px; text-align:center; background:#121915; border:1px solid #4c5b49; box-shadow:inset 0 1px #ffffff09; color:#d9dbb9; font-weight:600; font-variant-numeric:tabular-nums; }
    .gear-stat-row > span:last-child { color:#b3d2e3; border-color:#435c67; background:#131c21; }
    .gear-stat-row > span.item-stat-negative { color:#ed9b87; }
  `;
  document.head.appendChild(style);
}

function statRow(label: string, value: string, kind: string, tone = ""): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "item-stat-row";
  const key = document.createElement("span");
  key.className = "item-stat-key";
  key.append(icon(kind), label);
  const val = document.createElement("span");
  val.textContent = value;
  val.className = `item-stat-value ${tone}`;
  row.append(key, val);
  return row;
}

/** Shared item readout for hover popovers and the Bull's selected gear cards. */
export function itemStatsPanel(def: ItemDef, efficiencyPct?: number): HTMLDivElement {
  styles();
  const box = document.createElement("div");
  box.className = "item-stats";
  const stats = { ...def.stats };
  if (stats.attack !== undefined) {
    stats.meleeAttack ??= stats.attack;
    stats.magicAttack ??= stats.attack;
    delete stats.attack;
  }
  if (stats.atkMin !== undefined || stats.atkMax !== undefined) {
    box.append(statRow("Damage", `${stats.atkMin ?? 0}–${stats.atkMax ?? stats.atkMin ?? 0}`, "damage"));
  }
  if (stats.speedTicks !== undefined) {
    const speed = attackSpeedLabel(stats.speedTicks);
    box.append(statRow("Speed", speed, "speed", `item-stat-${speed.toLowerCase()}`));
  }
  delete stats.atkMin;
  delete stats.atkMax;
  delete stats.speedTicks;
  const keys = Object.keys(LABELS);
  for (const key of Object.keys(stats).sort((a, b) => {
    const ai = keys.indexOf(a), bi = keys.indexOf(b);
    return (ai < 0 ? keys.length : ai) - (bi < 0 ? keys.length : bi) || a.localeCompare(b);
  })) {
    const n = stats[key];
    if (n === undefined) continue;
    const bonus = key.endsWith("Attack") || key.endsWith("BonusPct");
    const value = `${bonus ? signed(n) : n}${key.endsWith("Pct") ? "%" : ""}`;
    const kind = key.endsWith("Attack") ? "attack" : key.endsWith("Def") ? "defence" : key === "maxHp" ? "health" : key === "range" ? "range" : "damage";
    box.append(statRow(LABELS[key] ?? key, value, kind, bonus ? n < 0 ? "item-stat-negative" : n > 0 ? "item-stat-positive" : "" : ""));
  }
  if (def.slot === "weapon" && efficiencyPct !== undefined) box.append(statRow("Proficiency", `${efficiencyPct}%`, "attack", efficiencyPct < 100 ? "item-stat-negative" : ""));
  return box;
}

/** Authored item stats, never guessed from appearance or a client-side kit. */
export function attachItemStats(host: HTMLElement, def: ItemDef, efficiencyPct?: number): void {
  styles();
  const tip = document.createElement("div");
  tip.className = "item-stat-tooltip";
  tip.id = `item-stat-tooltip-${++tooltipId}`;
  tip.hidden = true;
  tip.setAttribute("role", "tooltip");
  host.setAttribute("aria-describedby", tip.id);
  host.tabIndex = 0;
  host.removeAttribute("title");
  const name = document.createElement("strong");
  name.textContent = def.name;
  const sub = document.createElement("small");
  sub.textContent = `${SLOT_LABELS[def.slot]}${def.slot === "weapon" ? ` · ${def.weaponClass ?? "melee"}` : ""}`;
  tip.append(name, sub, itemStatsPanel(def, efficiencyPct));
  host.append(tip);
  const show = (): void => {
    tip.hidden = false;
    const rect = host.getBoundingClientRect();
    tip.style.left = `${Math.max(8, Math.min(window.innerWidth - tip.offsetWidth - 8, rect.left - tip.offsetWidth - 8))}px`;
    tip.style.top = `${Math.max(8, Math.min(window.innerHeight - tip.offsetHeight - 8, rect.top))}px`;
  };
  const hide = (): void => { tip.hidden = true; };
  host.addEventListener("pointerenter", show);
  host.addEventListener("pointerleave", hide);
  host.addEventListener("focus", show);
  host.addEventListener("blur", hide);
  host.addEventListener("pointerdown", hide);
  host.addEventListener("keydown", (event) => {
    if (event.key === "Escape") hide();
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      host.click();
    }
  });
}

/** Three paired aggregate rows, from the server or a draft using shared math. */
export function gearStatsPanel(stats: GearCombatStats): HTMLDivElement {
  styles();
  const box = document.createElement("div");
  box.className = "gear-stats";
  const header = document.createElement("div");
  header.className = "gear-stat-head";
  const blank = document.createElement("span");
  for (const [label, kind] of [["Melee", "attack"], ["Magic", "magic"]]) {
    const col = document.createElement("span");
    col.append(icon(kind!), label!);
    header.append(col);
  }
  header.prepend(blank);
  box.append(header);
  for (const [label, kind, melee, magic] of [
    ["Attack", "attack", String(stats.meleeAttack), String(stats.magicAttack)],
    ["Damage", "damage", `${signed(stats.meleeDamageBonusPct)}%`, `${signed(stats.magicDamageBonusPct)}%`],
    ["Defence", "defence", String(stats.meleeDef), String(stats.magicDef)],
  ]) {
    const row = document.createElement("div");
    row.className = "gear-stat-row";
    const key = document.createElement("span");
    key.append(icon(kind!), label!);
    row.append(key);
    for (const value of [melee, magic]) {
      const val = document.createElement("span");
      val.textContent = value!;
      if (value!.startsWith("-")) val.className = "item-stat-negative";
      row.append(val);
    }
    box.append(row);
  }
  return box;
}

export function itemEfficiency(def: ItemDef, stats?: GearCombatStats): number | undefined {
  if (!stats || def.slot !== "weapon") return undefined;
  return def.weaponClass === "magic" ? stats.magicWeaponEfficiencyPct : stats.meleeWeaponEfficiencyPct;
}
